import React, { ComponentProps } from "react";
import { Calendar } from "primereact/calendar";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../utils/constants/calendar-constants";

type CalendarDateChangeEvent = Parameters<
  NonNullable<ComponentProps<typeof Calendar>["onChange"]>
>[0];

export type DateRangeFilterVariant = "filter" | "stacked";

export interface DateRangeFilterProps {
  dateFrom?: Date | null;
  dateTo?: Date | null;
  onDateFromChange: (date: Date | null) => void;
  onDateToChange: (date: Date | null) => void;
  disabled?: boolean;
  startDateId?: string;
  endDateId?: string;
  showClearButtons?: boolean;
  columnClassName?: string;
  variant?: DateRangeFilterVariant;
  labelFrom?: string;
  labelTo?: string;
  dateFormat?: string;
  labelClassName?: string;
  calendarStyle?: React.CSSProperties;
  maxDateFrom?: Date;
  maxDateTo?: Date;
  minDateFrom?: Date;
  minDateTo?: Date;
}

interface DateFieldProps {
  id: string;
  label: string;
  value?: Date | null;
  onChange: (event: CalendarDateChangeEvent) => void;
  disabled: boolean;
  columnClassName: string;
  variant: DateRangeFilterVariant;
  dateFormat: string;
  labelClassName: string;
  calendarStyle?: React.CSSProperties;
  showClearButton: boolean;
  onClear: () => void;
  minDate?: Date;
  maxDate?: Date;
}

const DateField: React.FC<DateFieldProps> = ({
  id,
  label,
  value,
  onChange,
  disabled,
  columnClassName,
  variant,
  dateFormat,
  labelClassName,
  calendarStyle,
  showClearButton,
  onClear,
  minDate,
  maxDate,
}) => {
  const calendarKey = value ? value.toISOString() : `empty-${id}`;

  if (variant === "stacked") {
    const useFullWidth = !calendarStyle?.width;
    return (
      <div className={columnClassName}>
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
        <Calendar
          key={calendarKey}
          id={id}
          name={id}
          value={value ?? undefined}
          onChange={onChange}
          dateFormat={dateFormat}
          locale={CALENDAR_LOCALE}
          showIcon
          placeholder={`Seleccionar ${label.toLowerCase()}`}
          className={useFullWidth ? "w-full" : undefined}
          inputClassName={useFullWidth ? "w-full" : undefined}
          style={calendarStyle}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          aria-label={label}
        />
      </div>
    );
  }

  return (
    <div className={columnClassName}>
      {/* INC-02 — antes cada fecha traía un <Button icon="pi pi-times"
          severity="danger"> al costado, que además necesitaba p-inputgroup para
          quedar en línea. El Calendar limpia con su propia barra ("Hoy" /
          "Limpiar"), que no ocupa lugar en la grilla ni agrega un bloque de
          color al lado del campo. */}
      <span className="p-float-label">
        <Calendar
          key={calendarKey}
          className="w-full"
          id={id}
          name={id}
          dateFormat={dateFormat}
          locale={CALENDAR_LOCALE}
          onChange={onChange}
          value={value ?? undefined}
          hourFormat="24"
          hideOnDateTimeSelect
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          showIcon
          showButtonBar={showClearButton}
          onClearButtonClick={onClear}
        />
        <label htmlFor={id}>{label}</label>
      </span>
    </div>
  );
};

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  disabled = false,
  startDateId = "startDate",
  endDateId = "endDate",
  showClearButtons = true,
  variant = "filter",
  labelFrom = "Fecha desde",
  labelTo = "Fecha hasta",
  dateFormat = CALENDAR_DISPLAY_DATE_FORMAT,
  labelClassName = "block font-medium mb-2",
  calendarStyle,
  maxDateFrom,
  maxDateTo,
  minDateFrom,
  minDateTo,
  columnClassName =
    variant === "stacked"
      ? "col-12 md:col-6 lg:col-4"
      : "col-12 sm:col-6 lg:col-3 my-2",
}) => {
  const endDateMin = minDateTo ?? dateFrom ?? undefined;
  const handleDateFromChange = (event: CalendarDateChangeEvent) => {
    const value = event.value;
    onDateFromChange(value instanceof Date ? value : null);
  };

  const handleDateToChange = (event: CalendarDateChangeEvent) => {
    const value = event.value;
    onDateToChange(value instanceof Date ? value : null);
  };

  const showClear = variant === "filter" && showClearButtons;

  return (
    <>
      <DateField
        id={startDateId}
        label={labelFrom}
        value={dateFrom}
        onChange={handleDateFromChange}
        disabled={disabled}
        columnClassName={columnClassName}
        variant={variant}
        dateFormat={dateFormat}
        labelClassName={labelClassName}
        calendarStyle={calendarStyle}
        showClearButton={showClear}
        onClear={() => onDateFromChange(null)}
        minDate={minDateFrom}
        maxDate={maxDateFrom}
      />
      <DateField
        id={endDateId}
        label={labelTo}
        value={dateTo}
        onChange={handleDateToChange}
        disabled={disabled}
        columnClassName={columnClassName}
        variant={variant}
        dateFormat={dateFormat}
        labelClassName={labelClassName}
        calendarStyle={calendarStyle}
        showClearButton={showClear}
        onClear={() => onDateToChange(null)}
        minDate={endDateMin}
        maxDate={maxDateTo}
      />
    </>
  );
};
